import { getLogger } from '../commons/logger';
import { featureFlagsService } from '../commons/featureFlagsService';
import { getLabFeaturesByProjectId } from '../commons/testimServicesApi';
import type { ValueOf } from '../utils';
import type { LabFeatureFlagType } from '../commons/featureFlagsService';
import type { Plan } from 'services/src/commons/mongo/types/DbCompanyProjects';
import type { RawLabFeatureProject } from 'services/src/commons/mongo/models/labFeatureProject';

const logger = getLogger('lab-features-service');

class LabFeaturesService {
    featuresForProject: RawLabFeatureProject[] = [];
    labBatman = false;

    async loadLabFeatures(projectId: string, companyPlan: Plan) {
        if (!companyPlan || !projectId) {
            logger.error('missing companyPlan or projectId when loading lab features', { companyPlan, projectId });
            this.featuresForProject = [];
        }

        try {
            const labBatman = this.isLabsEnabledForCompany(companyPlan);
            const features = labBatman ? (await getLabFeaturesByProjectId(projectId)) : [];
            this.featuresForProject = features;
            this.labBatman = labBatman;
        } catch (err) {
            logger.error('failed loading lab features', { err, companyPlan, projectId });
            this.featuresForProject = [];
        }
    }

    isFeatureAvailableForProject(featureFlagName: keyof typeof featureFlagsService['flags']) {
        const featureFlag = featureFlagsService.flags[featureFlagName];
        this.validateAsLabFeatureFlag(featureFlag);
        const ffValue = (featureFlag as LabFeatureFlagType).getValue();
        if (ffValue === 'disabled') {
            return false;
        }
        if (ffValue === 'enabled') {
            return true;
        }

        const { featuresForProject: features, labBatman } = this;
        const labFeature = features.find(f => f.featureFlagName === featureFlagName);
        const featureEnabled = labFeature?.enabled;

        return Boolean(labBatman && featureEnabled);
    }

    isLabsEnabledForCompany(companyPlan: Plan) {
        return Boolean(companyPlan?.premiumFeatures?.enableLabFeatures);
    }

    validateAsLabFeatureFlag(featureFlag: ValueOf<typeof featureFlagsService['flags']>) {
        if ('getValue' in featureFlag) {
            return;
        }

        const msg = `Attempted querying a lab feature flag which isn't a variant. This means that a wrong configuration is being used in FeatureFlagsService (for feature flag: ${featureFlag.name}`;
        logger.error(msg, { featureFlagName: featureFlag.name });
        throw new Error(msg);
    }
}
export const labFeaturesService = new LabFeaturesService();
